// 학생-운영자 1:1 채팅 (학생방)

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Paper, Typography, TextField, IconButton, CircularProgress } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

interface Comment {
  id: string;
  author_type: string;
  author_name: string;
  content: string;
  created_at: string;
}
interface ThreadPost {
  id: string;
  title: string;
  content: string;
  author_name: string;
  created_at: string;
}
interface PostListItem {
  id: string;
  title: string;
  content: string;
  author_name: string;
  created_at: string;
  is_read: boolean;
  comment_count: number;
}

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('student_access_token') || ''}`,
  'Content-Type': 'application/json',
});

const fmt = (s: string) => {
  if (!s) return '';
  const d = new Date(s);
  return d.toLocaleString('ko-KR', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
};

const ChatPage: React.FC = () => {
  const [list, setList] = useState<PostListItem[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [post, setPost] = useState<ThreadPost | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [loadingThread, setLoadingThread] = useState(false);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const loadList = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE_URL}/api/v1/student/posts`, { headers: authHeaders() });
      if (r.ok) {
        const j = await r.json();
        const posts: PostListItem[] = j?.data?.posts || [];
        setList(posts);
        if (posts.length === 1) setActiveId(posts[0].id);
      }
    } catch (e) {
      console.error('대화 목록 로드 실패:', e);
    } finally {
      setLoadingList(false);
    }
  }, []);

  const loadThread = useCallback(async (id: string, silent = false) => {
    if (!silent) setLoadingThread(true);
    try {
      const r = await fetch(`${API_BASE_URL}/api/v1/student/posts/${id}`, { headers: authHeaders() });
      if (r.ok) {
        const j = await r.json();
        setPost(j?.data?.post || null);
        const fresh: Comment[] = j?.data?.comments || [];
        setComments((prev) => (prev.length !== fresh.length || !silent ? fresh : prev));
      }
    } catch (e) {
      console.error('대화 로드 실패:', e);
    } finally {
      if (!silent) setLoadingThread(false);
    }
  }, []);

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    if (activeId) loadThread(activeId);
  }, [activeId, loadThread]);

  // 포그라운드 한정 경량 폴링 (~8초)
  useEffect(() => {
    if (!activeId) return;
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') loadThread(activeId, true);
    }, 8000);
    return () => clearInterval(timer);
  }, [activeId, loadThread]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [comments, post]);

  const send = async () => {
    const text = input.trim();
    if (!text || !activeId || sending) return;
    setSending(true);
    try {
      const r = await fetch(`${API_BASE_URL}/api/v1/student/posts/${activeId}/comments`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ content: text }),
      });
      if (r.ok) {
        setInput('');
        await loadThread(activeId);
      } else {
        const e = await r.json();
        alert(e.detail || '전송에 실패했습니다.');
      }
    } catch (e) {
      console.error('전송 실패:', e);
      alert('전송 중 오류가 발생했습니다.');
    } finally {
      setSending(false);
    }
  };

  if (loadingList) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  // 목록 화면 (스레드 미선택 + 다건)
  if (!activeId) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
          선생님 메시지
        </Typography>
        {list.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>
            받은 메시지가 없습니다.
          </Typography>
        ) : (
          list.map((p) => (
            <Paper
              key={p.id}
              onClick={() => setActiveId(p.id)}
              sx={{ p: 2, mb: 1, cursor: 'pointer', borderRadius: 2, '&:hover': { bgcolor: '#f5f5f5' } }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography fontWeight={p.is_read ? 400 : 700}>{p.title}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {fmt(p.created_at)}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" noWrap sx={{ mt: 0.5 }}>
                {p.content}
              </Typography>
            </Paper>
          ))
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 120px)' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.5, borderBottom: '1px solid #eee' }}>
        {list.length > 1 && (
          <IconButton size="small" onClick={() => setActiveId(null)}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
        )}
        <Typography fontWeight={700}>{post?.author_name || '선생님'}</Typography>
      </Box>

      <Box ref={scrollRef} sx={{ flex: 1, overflowY: 'auto', p: 2, bgcolor: '#fafafa' }}>
        {loadingThread ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={22} />
          </Box>
        ) : (
          <>
            {post && (
              <Bubble mine={false} name={post.author_name} content={post.content} time={post.created_at} />
            )}
            {comments.map((c) => (
              <Bubble
                key={c.id}
                mine={c.author_type === 'student'}
                name={c.author_name}
                content={c.content}
                time={c.created_at}
              />
            ))}
            {!post && comments.length === 0 && (
              <Typography color="text.secondary" sx={{ textAlign: 'center', py: 6 }}>
                대화가 없습니다.
              </Typography>
            )}
          </>
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 1, p: 1.5, borderTop: '1px solid #eee', alignItems: 'flex-end' }}>
        <TextField
          fullWidth
          size="small"
          multiline
          maxRows={4}
          placeholder="메시지를 입력하세요"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
        />
        <IconButton color="primary" disabled={sending || !input.trim()} onClick={send}>
          {sending ? <CircularProgress size={20} /> : <SendIcon />}
        </IconButton>
      </Box>
    </Box>
  );
};

const Bubble: React.FC<{ mine: boolean; name: string; content: string; time: string }> = ({
  mine,
  name,
  content,
  time,
}) => (
  <Box sx={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start', mb: 1.25 }}>
    <Box sx={{ maxWidth: '78%', display: 'flex', flexDirection: 'column', alignItems: mine ? 'flex-end' : 'flex-start' }}>
      {!mine && (
        <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5, mb: 0.25 }}>
          {name}
        </Typography>
      )}
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.75, flexDirection: mine ? 'row' : 'row-reverse' }}>
        <Typography variant="caption" sx={{ color: '#aaa', whiteSpace: 'nowrap', fontSize: 10 }}>
          {fmt(time)}
        </Typography>
        <Box
          sx={{
            px: 1.5,
            py: 1,
            borderRadius: 2,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            bgcolor: mine ? '#2563EB' : '#fff',
            color: mine ? '#fff' : '#222',
            border: mine ? 'none' : '1px solid #e0e0e0',
          }}
        >
          {content}
        </Box>
      </Box>
    </Box>
  </Box>
);

export default ChatPage;
